import { createSlice } from '@reduxjs/toolkit';
import { AdmitBaseState, checkIfPatientIsTemporary, getInitialAdmission } from '../../pages/admit/admitUtils';
import { setSnackbar } from './globals';
import { RequestDataParser, SelectPatientParser } from '../../pages/admit/admitDataParsers';
import moment from 'moment';
import _ from 'lodash';
import API from '../../app/api';
import { setLoading as setLoadingGlobal, setMergeQueuePatient } from './patient';
import { sendUserAudit } from './user';
import { AUDIT_TYPES } from '../../util/auditUtil';
import { PATIENT_TAGS } from '../../util/patientUtil';
import { toggleSplitScreen } from './patientContext';
import { updateAppointmentStatus } from './appointments';
import { SLOT_STATUSES } from '../../pages/appointments/utils/constants/appointmentSessionConstants';
import { DEFAULT_STATE_CSM } from '../../pages/ward/forms/csm/csmFormUtil';
import { REGISTRATION_FLOW_TYPE } from '../../pages/admit/components/registrationFlow/regFlowUtil';
import APIForm from '../../app/apiForm';

export const INIT_SEARCH_ADMIT_STATE = {
    loading: false,
    results: undefined,
    tempIds: [],
    page: 1,
    sort: { type: '', direction: true },
    completedForm: [],
    tempString: null,
};

export const INIT_PATIENT_ADMISSION_STATE = {
    patient: null,
    openDialog: false,
};

// temporaryPatientAttendanceRef
// temporaryPatientRef
export const INIT_TEMPORARY_PATIENT_ADMISSION_STATE = {
    attendance: null,
    patient: null,
    patientNumbers: null,
};

// setTemporaryMergePt(patientRef.current?.hospitalNumber);
// patientMergeRef.current = patientRef.current;
// setIsMergeActive(true);
export const INIT_MERGE_ADMISSION_STATE = {
    patient: null,
    patientId: null,
    mergeActive: false,
    mergeToNewPatient: false,
};

const INIT_STATE = {
    admissionForm: AdmitBaseState,
    admissionAttendanceForm: getInitialAdmission(),
    temporaryAdmissionData: INIT_TEMPORARY_PATIENT_ADMISSION_STATE,
    tempPt: null,
    admissionBedBookingData: {},
    admissionBedBookingForm: DEFAULT_STATE_CSM,
    admissionMergeForm: INIT_MERGE_ADMISSION_STATE,
    admissionPatientObject: null,
    patientSelected: false,
    patientActiveAttendance: false, //patientOnWhiteboard
    selectedActivityToLink: null,
    appointmentLinkedAttendance: null,

    step: 0,
    selectedHospital: null,
    locationType: null,
    search: INIT_SEARCH_ADMIT_STATE,
    showPatientContext: false,
    showCompletedForm: false,

    isFastRegistry: false,
    isSpeedRegistry: false,
    isBedBooking: false,
    isBedas: false,
    isSDEC: false,
    isUTC: false,
    isReAttendance: false,
    isAppointment: false,
    isTemporaryPatientEdit: false,
    isOnlyAddPatient: false,
    isOnlyDemographic: false,
    isUpdateActivity: false,
    isMergeActive: false,
    isSamePatientToMerge: false,
    isEditFormList: false,

    toggleNFA: false,
    toggleNFATemporary: false,
    createPatientAdmission: INIT_PATIENT_ADMISSION_STATE,
    appointmentSlotRef: null,

    openGenderMandatoryDialog: false,
    openTemporaryPatientEditDialog: false,
    openCreatePatientDialog: false,
    openAttendanceErrorDialog: false,
    openAddTemporaryPatientDialog: false,
    openAppointmentDialog: false,
    openActivityListDialog: false,
    openPriorityList: false,
    openDischargeList: false,

    openPatientWarningActiveActivityDialog: false,
    isLoadingActivity: false,
};

export const admissionSlice = createSlice({
    name: 'admission',
    initialState: INIT_STATE,
    reducers: {
        setAdmissionForm: (state, action) => {
            state.admissionForm = action.payload;
        },
        setAdmissionPatientObject: (state, action) => {
            state.admissionPatientObject = action.payload;
        },
        updateAdmissionForm: (state, action) => {
            state.admissionForm = {
                ...state.admissionForm,
                ...action.payload,
            };
        },
        setAdmissionAttendanceForm: (state, action) => {
            state.admissionAttendanceForm = action.payload;
        },
        setAdmissionTempPt: (state, action) => {
            state.tempPt = action.payload;
        },
        updateAdmissionAttendanceForm: (state, action) => {
            state.admissionAttendanceForm = {
                ...state.admissionAttendanceForm,
                ...action.payload,
            };
        },
        updateAdmissionSearchState: (state, action) => {
            state.search = {
                ...state.search,
                ...action.payload,
            };
        },
        updatePatientAdmission: (state, action) => {
            state.createPatientAdmission = {
                ...state.createPatientAdmission,
                ...action.payload,
            };
        },
        setAdmissionBedBookingData: (state, action) => {
            state.admissionBedBookingData = action.payload;
        },
        updateAdmissionBedBookingData: (state, action) => {
            state.admissionBedBookingData = {
                ...state.admissionBedBookingData,
                ...action.payload,
            };
        },
        updateAdmissionBedBookingForm: (state, action) => {
            state.admissionBedBookingForm = {
                ...state.admissionBedBookingForm,
                ...action.payload,
            };
        },

        setSelectedActivityToLink: (state, action) => {
            state.selectedActivityToLink = action.payload;
        },
        setAdmissionStep: (state, action) => {
            state.step = action.payload;
        },
        setAdmissionSelectedHospital: (state, action) => {
            state.selectedHospital = action.payload;
        },
        setAdmissionLocationType: (state, action) => {
            state.locationType = action.payload;
        },
        setShowPatientContext: (state, action) => {
            state.showPatientContext = action.payload;
        },
        setShowCompletedForm: (state, action) => {
            state.showCompletedForm = action.payload;
        },
        setAppointmentLinkedAttendance: (state, action) => {
            state.appointmentLinkedAttendance = action.payload;
        },

        setIsFastRegistry: (state, action) => {
            state.isFastRegistry = action.payload;
        },
        setIsSpeedRegistry: (state, action) => {
            state.isSpeedRegistry = action.payload;
        },
        setIsBedBooking: (state, action) => {
            state.isBedBooking = action.payload;
        },
        setIsBedas: (state, action) => {
            state.isBedas = action.payload;
        },
        setIsSDEC: (state, action) => {
            state.isSDEC = action.payload;
        },
        setIsUTC: (state, action) => {
            state.isUTC = action.payload;
        },
        setIsReAttendance: (state, action) => {
            state.isReAttendance = action.payload;
        },
        setIsTemporaryPatientEdit: (state, action) => {
            state.isTemporaryPatientEdit = action.payload;
        },
        setIsOnlyAddPatient: (state, action) => {
            state.isOnlyAddPatient = action.payload;
        },
        setIsAppointment: (state, action) => {
            state.isAppointment = action.payload;
        },
        setIsOnlyDemographic: (state, action) => {
            state.isOnlyDemographic = action.payload;
        },
        setIsUpdateActivity: (state, action) => {
            state.isUpdateActivity = action.payload;
        },
        setIsMergeActive: (state, action) => {
            state.isMergeActive = action.payload;
        },
        setIsSamePatientToMerge: (state, action) => {
            state.isSamePatientToMerge = action.payload;
        },
        setIsEditFormList: (state, action) => {
            state.isEditFormList = action.payload;
        },

        setToggleNFA: (state, action) => {
            state.toggleNFA = action.payload;
        },
        setToggleNFATemporary: (state, action) => {
            state.toggleNFATemporary = action.payload;
        },
        setPatientSelected: (state, action) => {
            state.patientSelected = action.payload;
        },
        setPatientActiveAttendance: (state, action) => {
            state.patientActiveAttendance = action.payload;
        },
        setAppointmentSlotRef: (state, action) => {
            state.appointmentSlotRef = action.payload;
        },
        updateTemporaryAdmissionData: (state, action) => {
            state.temporaryAdmissionData = {
                ...state.temporaryAdmissionData,
                ...action.payload,
            };
        },
        updateAdmissionMergeForm: (state, action) => {
            state.admissionMergeForm = {
                ...state.admissionMergeForm,
                ...action.payload,
            };
        },

        setOpenGenderMandatoryDialog: (state, action) => {
            state.openGenderMandatoryDialog = action.payload;
        },
        setOpenTemporaryPatientEditDialog: (state, action) => {
            state.openTemporaryPatientEditDialog = action.payload;
        },
        setOpenCreatePatientDialog: (state, action) => {
            state.openCreatePatientDialog = action.payload;
        },
        setOpenAttendanceErrorDialog: (state, action) => {
            state.openAttendanceErrorDialog = action.payload;
        },
        setOpenAddTemporaryPatientDialog: (state, action) => {
            state.openAddTemporaryPatientDialog = action.payload;
        },
        setOpenAppointmentDialog: (state, action) => {
            state.openAppointmentDialog = action.payload;
        },
        setOpenActivityListDialog: (state, action) => {
            state.openActivityListDialog = action.payload;
        },
        setOpenPriorityList: (state, action) => {
            state.openPriorityList = action.payload;
        },
        setOpenPatientWarningActiveActivityDialog: (state, action) => {
            state.openPatientWarningActiveActivityDialog = action.payload;
        },
        setIsLoadingActivity: (state, action) => {
            state.isLoadingActivity = action.payload;
        },
        setOpenDischargeList: (state, action) => {
            state.openDischargeList = action.payload;
        },
    },
});

// Setters
export const {
    setAdmissionForm,
    updateAdmissionForm,
    setAdmissionAttendanceForm,
    updateAdmissionAttendanceForm,
    setAdmissionStep,
    setAdmissionLocationType,
    setAdmissionSelectedHospital,
    updateAdmissionSearchState,
    setShowPatientContext,
    setShowCompletedForm,
    updatePatientAdmission,
    updateAdmissionMergeForm,
    setAdmissionPatientObject,
    setAdmissionTempPt,
    setAdmissionBedBookingData,
    updateAdmissionBedBookingData,
    setSelectedActivityToLink,
    setAppointmentLinkedAttendance,

    setIsFastRegistry,
    setIsSpeedRegistry,
    setIsBedas,
    setIsSDEC,
    setIsUTC,
    setIsReAttendance,
    setIsOnlyAddPatient,
    setIsAppointment,
    setIsBedBooking,
    setIsTemporaryPatientEdit,
    setIsOnlyDemographic,
    setIsMergeActive,
    setIsUpdateActivity,
    setIsSamePatientToMerge,
    setIsEditFormList,

    setToggleNFATemporary,
    setToggleNFA,
    updateTemporaryAdmissionData,
    setPatientSelected,
    setPatientActiveAttendance,
    updateAdmissionBedBookingForm,
    setAppointmentSlotRef,

    setOpenGenderMandatoryDialog,
    setOpenTemporaryPatientEditDialog,
    setOpenCreatePatientDialog,
    setOpenAttendanceErrorDialog,
    setOpenAddTemporaryPatientDialog,
    setOpenAppointmentDialog,
    setOpenActivityListDialog,
    setOpenPriorityList,
    setOpenDischargeList,

    setOpenPatientWarningActiveActivityDialog,
    setIsLoadingActivity,
} = admissionSlice.actions;

// Variable
export const admissionForm = (state) => state.admission.admissionForm;
export const admissionAttendanceForm = (state) => state.admission.admissionAttendanceForm;
export const admissionPatientObject = (state) => state.admission.admissionPatientObject;
export const admissionTemporaryData = (state) => state.admission.temporaryAdmissionData;
export const admissionBedBookingForm = (state) => state.admission.admissionBedBookingForm;
export const admissionBedBookingData = (state) => state.admission.admissionBedBookingData;
export const admissionPatientIsSelected = (state) => state.admission.patientSelected;
export const admissionPatientActiveAttendance = (state) => state.admission.patientActiveAttendance; //patientOnWhiteboard
export const admissionMergeForm = (state) => state.admission.admissionMergeForm;
export const admissionTempPt = (state) => state.admission.tempPt;
export const admissionSelectedActivityToLink = (state) => state.admission.selectedActivityToLink;
export const admissionAppointmentLinkedAttendance = (state) => state.admission.appointmentLinkedAttendance;

export const admissionStep = (state) => state.admission.step;
export const admissionSelectedHospital = (state) => state.admission.selectedHospital;
export const admissionLocationType = (state) => state.admission.locationType;
export const admissionSearch = (state) => state.admission.search;
export const admissionShowPatientContext = (state) => state.admission.showPatientContext;
export const admissionShowCompletedForm = (state) => state.admission.showCompletedForm;

export const admissionIsFastRegistry = (state) => state.admission.isFastRegistry;
export const admissionIsSpeedRegistry = (state) => state.admission.isSpeedRegistry;
export const admissionIsBedBooking = (state) => state.admission.isBedBooking; //isBedBookingChosen
export const admissionIsBedas = (state) => state.admission.isBedas;
export const admissionIsSDEC = (state) => state.admission.isSDEC;
export const admissionIsUTC = (state) => state.admission.isUTC;
export const admissionIsReAttendance = (state) => state.admission.isReAttendance;
export const admissionIsAppointment = (state) => state.admission.isAppointment;
export const admissionIsTemporaryPatientEdit = (state) => state.admission.isTemporaryPatientEdit;
export const admissionIsOnlyAddPatient = (state) => state.admission.isOnlyAddPatient;
export const admissionIsOnlyDemographic = (state) => state.admission.isOnlyDemographic;
export const admissionIsUpdateActivity = (state) => state.admission.isUpdateActivity;
export const admissionIsMergeActive = (state) => state.admission.isMergeActive;
export const admissionIsSamePatientToMerge = (state) => state.admission.isSamePatientToMerge;
export const admissionIsEditFormList = (state) => state.admission.isEditFormList;

export const admissionToggleNFA = (state) => state.admission.toggleNFA;
export const admissionToggleNFATemporary = (state) => state.admission.toggleNFATemporary;
export const admissionPatientVisitState = (state) => state.admission.createPatientAdmission;
export const admissionAppointmentSlotRef = (state) => state.admission.appointmentSlotRef;

export const admissionGenderMandatoryDialogOpen = (state) => state.admission.openGenderMandatoryDialog;
export const admissionTemporaryPatientEditDialogOpen = (state) => state.admission.openTemporaryPatientEditDialog;
export const admissionCreatePatientDialogOpen = (state) => state.admission.openCreatePatientDialog;
export const admissionAttendanceErrorDialogOpen = (state) => state.admission.openAttendanceErrorDialog;
export const admissionAddTemporaryPatientDialogOpen = (state) => state.admission.openAddTemporaryPatientDialog;
export const admissionAppointmentDialogOpen = (state) => state.admission.openAppointmentDialog;
export const admissionActivityListDialogOpen = (state) => state.admission.openActivityListDialog;
export const admissionPriorityListDialogOpen = (state) => state.admission.openPriorityList;
export const admissionDischargeListDialogOpen = (state) => state.admission.openDischargeList;
export const admissionIsLoadingActivity = (state) => state.admission.isLoadingActivity;

export const admissionPatientWarningActiveActivityDialogOpen = (state) => state.admission.openPatientWarningActiveActivityDialog;

export const clearAdmission = () => (dispatch) => {
    dispatch(setAdmissionStep(0));
    dispatch(setAdmissionForm(AdmitBaseState));
    dispatch(setAdmissionAttendanceForm(getInitialAdmission()));
    dispatch(setAdmissionLocationType(null));
    dispatch(setAdmissionPatientObject(null));
    dispatch(setAdmissionTempPt(null));
    dispatch(updateTemporaryAdmissionData(INIT_TEMPORARY_PATIENT_ADMISSION_STATE));
    dispatch(setAdmissionBedBookingData({}));
    dispatch(setShowPatientContext(false));
    dispatch(setSelectedActivityToLink(null));
    dispatch(setAppointmentLinkedAttendance(null));

    // Flags
    dispatch(setShowCompletedForm(false));
    dispatch(setIsFastRegistry(false));
    dispatch(setIsSpeedRegistry(false));
    dispatch(setIsBedBooking(false));
    dispatch(setIsBedas(false));
    dispatch(setIsSDEC(false));
    dispatch(setIsUTC(false));
    dispatch(setIsReAttendance(false));
    dispatch(setIsAppointment(false));
    dispatch(setIsOnlyAddPatient(false));
    dispatch(setIsTemporaryPatientEdit(false));
    dispatch(setIsOnlyDemographic(false));
    dispatch(setIsUpdateActivity(false));
    dispatch(setIsMergeActive(false));
    dispatch(setIsSamePatientToMerge(false));
    dispatch(setIsEditFormList(false));

    // Others
    dispatch(setToggleNFA(false));
    dispatch(setToggleNFATemporary(false));
    dispatch(setPatientSelected(false));
    dispatch(setPatientActiveAttendance(false));
    dispatch(setAppointmentSlotRef(null));
    dispatch(setIsLoadingActivity(false));

    // States
    dispatch(updatePatientAdmission(INIT_PATIENT_ADMISSION_STATE));
    dispatch(updateAdmissionSearchState(INIT_SEARCH_ADMIT_STATE));
    dispatch(updateAdmissionBedBookingForm(DEFAULT_STATE_CSM));
    dispatch(updateAdmissionMergeForm(INIT_MERGE_ADMISSION_STATE));

    // Close all dialogues
    dispatch(setOpenGenderMandatoryDialog(false));
    dispatch(setOpenCreatePatientDialog(false));
    dispatch(setOpenTemporaryPatientEditDialog(false));
    dispatch(setOpenAttendanceErrorDialog(false));
    dispatch(setOpenAddTemporaryPatientDialog(false));
    dispatch(setOpenAppointmentDialog(false));
    dispatch(setOpenActivityListDialog(false));
};

// Old selectPatient
export const selectAdmissionPatient =
    (patient, ptFromPriority = false, activeStep = 1) =>
    (dispatch, getState) => {
        const speedRegFlag = getState().admission.isSpeedRegistry;
        const fastRegFlag = getState().admission.isFastRegistry;
        const ethnicOptions = getState().dicts.ethnic;
        const genderOptions = getState().dicts.gender;
        const languageOptions = getState().dicts.languages;

        const haveActiveAtt = patient?.activeAttendances
            ? !_.isEmpty(_.filter(patient?.activeAttendances, (pt) => pt?.attendanceType === 'E' && _.isNil(pt?.endDate)))
            : false;
        const pastAttendanceView = !haveActiveAtt;
        dispatch(setPatientActiveAttendance(haveActiveAtt));

        const baseAdmitState = AdmitBaseState;
        const homeAddressIndex = patient.addresses?.findIndex((address) => address?.typeName === 'H' || address?.typeName === 'Home');
        const homeAddress = patient.addresses?.[homeAddressIndex];
        const temporaryAddressIndex = patient.addresses?.findIndex(
            (address) => address?.typeName === 'TMP' || address?.typeName === 'Temporary',
        );
        const temporaryAddress = patient.addresses?.[temporaryAddressIndex];

        const [street, city, postTown, county] = SelectPatientParser.getAddresses(homeAddress?.lines ?? homeAddress?.addressLines);

        const [streetTemporary, cityTemporary, postTownTemporary, countyTemporary] = SelectPatientParser.getAddresses(
            temporaryAddress?.lines ?? temporaryAddress?.addressLines,
            true,
        );

        const findEthnicGroup = (code) => ethnicOptions?.find((ethnicGroup) => ethnicGroup.__code === code) || null;
        const findGender = (code) => genderOptions.find((gender) => gender.__code === code);

        const selectedPatient = {
            ...baseAdmitState,
            patientId: patient.__id,
            hospitalNumber: SelectPatientParser.getHospitalNumber(patient) || patient.hospitalNumber,
            nhsNumber: SelectPatientParser.getNHSNumber(patient),
            firstName: patient.firstName,
            middleNames: patient.middleNames || '',
            familyName: patient.familyName,
            dob: moment(patient.dob),
            ethnicGroup: patient.ethnicGroup ? findEthnicGroup(patient.ethnicGroup.__code) : null,
            gender: findGender(patient.genderCode),
            addressLines: patient.addressLines ? patient.addressLines : '',
            city: city,
            county: county,
            postTown: postTown,
            street: street,
            names: patient?.names || null,
            patientNeedsInterpreter: patient.interpreterRefused ? null : patient.interpreterRequired || false,
            spokenLanguage: patient.primaryLanguageCode
                ? _.find(languageOptions, { __code: patient.primaryLanguageCode })
                : baseAdmitState.spokenLanguage,
            spokenLanguageExists: !!patient.primaryLanguageCode,
            interpreterLanguage: patient.interpreterLanguageCode
                ? _.find(languageOptions, { __code: patient.interpreterLanguageCode })
                : baseAdmitState.interpreterLanguage,
            postalCode: homeAddress ? homeAddress?.postalCode : patient?.postalCode || '',
            ehicNumber: SelectPatientParser.getEhicNumber(patient),
            occupation: patient.occupation ? patient.occupation : '',
            phoneNumbers: SelectPatientParser.getPhoneNumbers(patient),
            gpHref: SelectPatientParser.getGPId(patient),
            surgeryHref: SelectPatientParser.getSurgeryId(patient),
            nokList: SelectPatientParser.getNextOfKins(patient),
            emailAdresses: SelectPatientParser.getEmail(patient),
            school: patient.school ? patient.school : '',
            schoolName: patient.schoolName ? patient.schoolName : patient.school ? patient.school.name : '',
            schoolContact: patient.schoolContact ? patient.schoolContact : patient.school ? patient.school.contact : '',
            schoolAddress: patient.schoolAddress
                ? patient.schoolAddress
                : patient.school
                  ? patient.school.address.addressLines.join(', ')
                  : '',
            schoolPostalCode: patient.schoolPostalCode ? patient.schoolPostalCode : patient.school ? patient.school.address.postalCode : '',
            tags: patient?.tags ? patient?.tags : [],
            overseasStatus: patient?.overseasVisitorChargingCategory || null,
            accommodationStatus: patient?.accommodationStatus || null,
            activeAttendances: _.filter(patient?.activeAttendances, (att) => att?.attendanceType === 'E'),
            streetTemporary,
            cityTemporary,
            postTownTemporary,
            countyTemporary,
            postalCodeTemporary: temporaryAddress?.postalCode ? temporaryAddress?.postalCode : null,
        };

        if (patient?.patientNumbers) {
            dispatch(
                updateTemporaryAdmissionData({
                    patientNumbers: patient.patientNumbers,
                }),
            );
        }

        if (selectedPatient?.postalCode && _.startsWith(selectedPatient?.postalCode, 'ZZ99')) {
            dispatch(setToggleNFA(true));
        }

        if (selectedPatient?.postalCodeTemporary && _.startsWith(selectedPatient?.postalCodeTemporary, 'ZZ99')) {
            dispatch(setToggleNFATemporary(true));
        }

        dispatch(updateAdmissionForm(selectedPatient));
        dispatch(
            getAdmissionAttendance(selectedPatient, ptFromPriority, pastAttendanceView ? ptFromPriority?.admission?.attendanceId : null),
        );

        dispatch(setPatientSelected(true));
        dispatch(setAdmissionStep(activeStep));

        // Clear search
        dispatch(updateAdmissionSearchState(INIT_SEARCH_ADMIT_STATE));

        if (!fastRegFlag && (!patient.gender || (patient.gender && patient.gender !== 'Male' && patient.gender !== 'Female'))) {
            dispatch(setOpenGenderMandatoryDialog(true));
        }

        if (!fastRegFlag && !speedRegFlag) {
            dispatch(setSnackbar({ type: 'info', message: "Please verify patient's information is current" }));
        }
    };

// Old getAttendanceValues
export const getAdmissionAttendance = (patient, ptFromPriority, attendanceId) => async (dispatch, getState) => {
    dispatch(setIsLoadingActivity(true));

    //Dict
    const hospitals = getState().ward.mapHospitalList;
    const chiefComplaintDict = getState().dicts.ecds.chiefComplaint;
    const arrivalModeDict = getState().dicts.ecds.arrivalMode;
    const attendanceSourceDict = getState().dicts.ecds.attendanceSource;
    const attendanceCategoryDict = getState().dicts.attendanceCategories;
    const edActivityTypes = getState().dicts.activityTypeED;
    const attendanceTypeOptions = getState().dicts.admissionMethods;
    const locationOptions = getState().dicts.attendanceTypes;
    const isUpdateActivity = getState().admission.isUpdateActivity;

    const registrationConfig = getState().externalConfig.registrationCfg;

    const activeAttendance = patient?.activeAttendances?.[patient.activeAttendances.length - 1];
    const patientAttendanceData = {
        attendanceId: (attendanceId ?? activeAttendance?.attendanceId) || null,
        patientId: patient.hospitalNumber || patient.__id,
    };

    const wardData = await API.getWardsAttendances(['E']);
    let patientInWard = _.find(wardData, (wardAtt) => wardAtt?.patient?.hospitalNumber === patient?.hospitalNumber);

    // INIT DATA
    dispatch(setAdmissionAttendanceForm(getInitialAdmission(registrationConfig)));

    let attendanceSource,
        attendanceAssaultRelated,
        attendanceType,
        location,
        modeOfArrival,
        chiefComplaint,
        careContactIdentifier,
        conveyingAmbulanceOrgId,
        ambulanceIncidentNumber,
        admitDate,
        cpIs,
        cpIsAlertPresent,
        attType,
        wardId,
        edActivityType;
    try {
        const attendanceData =
            activeAttendance || isUpdateActivity
                ? await API.getPatientAttendance(patientAttendanceData).then((res) => {
                      return res?.[0];
                  })
                : null;
        if (!patientInWard && ptFromPriority) patientInWard = ptFromPriority;

        if (attendanceData) {
            attType = attendanceData.attendanceType;

            if (attendanceData?.facility && !_.isEmpty(hospitals)) {
                const find = hospitals.find((h) => h.__id === attendanceData?.facility);
                dispatch(setAdmissionSelectedHospital(find || null));
            }

            const historyOfPresentingComplaint = SelectPatientParser.getHistoryOfPresentingComplaint(patientInWard);
            const attendanceIdFromPAS = attendanceData?.__id;

            if (attendanceId) {
                dispatch(
                    updateTemporaryAdmissionData({
                        attendance: attendanceData,
                    }),
                );
            }

            let the111flag;
            let majorTraumaFlag;
            const escortArray = patientInWard?.metadata?.escort || [];
            const patientFlags = patientInWard?.metadata?.patientFlags || [];
            let attendanceCategory = patientInWard?.metadata?.attendanceCategory || null;

            if (attendanceCategory && attendanceCategory?.code && !attendanceCategory?.__code) {
                attendanceCategory = _.find(attendanceCategoryDict, { __code: attendanceCategory?.code }) || null;
            }

            if (!_.isEmpty(patientFlags)) {
                the111flag = patientFlags.find((flag) => flag?.name === '111');
                majorTraumaFlag = patientFlags.find((flag) => flag?.name === 'majorTrauma');
            }

            if (!_.isEmpty(arrivalModeDict)) {
                modeOfArrival = arrivalModeDict.find(
                    (option) => option.__code === attendanceData?.arrivalModeCode || option.snomedCode === attendanceData?.arrivalModeCode,
                );
            }

            if (!_.isEmpty(chiefComplaintDict)) {
                chiefComplaint = chiefComplaintDict.find((option) => option.__code === attendanceData?.initialAEComplaintCode);
            }

            if (!_.isEmpty(attendanceSourceDict)) {
                attendanceSource = attendanceSourceDict.find((option) => option.snomedCode === attendanceData?.admitSourceCode);
            }

            if (!_.isEmpty(attendanceTypeOptions)) {
                attendanceType = attendanceTypeOptions.find((option) => option.__code === attendanceData?.admissionTypeCode);
            }

            if (!_.isEmpty(locationOptions)) {
                location = locationOptions.find((option) => option.__code === attendanceData?.attendanceType);
            }

            if (attendanceData.careContactIdentifier) {
                careContactIdentifier = attendanceData.careContactIdentifier;
            }

            if (attendanceData.conveyingAmbulanceOrgId) {
                conveyingAmbulanceOrgId = attendanceData.conveyingAmbulanceOrgId;
            }

            if (attendanceData.ambulanceIncidentNumber) {
                ambulanceIncidentNumber = attendanceData.ambulanceIncidentNumber;
            }

            if (attendanceData?.attendanceAssaultRelated) {
                attendanceAssaultRelated = attendanceData?.attendanceAssaultRelated || false;
            }

            if (attendanceData.startDate) {
                admitDate = moment(attendanceData.startDate);
            }

            if (attendanceData?.cpisFlag) {
                cpIs = true;
                cpIsAlertPresent = attendanceData?.cpisFlag === 'Y';
            }

            if (attendanceData?.edActivityType) {
                const find = edActivityTypes?.find((t) => t.__code === attendanceData?.edActivityType);
                edActivityType = find || null;
            }

            if (attendanceData?.pointOfCare) {
                wardId = attendanceData?.pointOfCare;
            }

            let updatedAdmissionObj = {
                attendanceSource,
                attendanceType,
                location,
                modeOfArrival,
                chiefComplaint,
                careContactIdentifier,
                conveyingAmbulanceOrgId,
                ambulanceIncidentNumber,
                historyOfPresentingComplaint,
                lastAttendanceId: attendanceIdFromPAS,
                admitDate,
                cpIs,
                cpIsAlertPresent,
                escortArray,
                patientFlags,
                attendanceCategory,
                assaultRelated: attendanceAssaultRelated,
                edActivityType,
                wardId,
                __attendanceType: attType,
            };

            if (the111flag) {
                updatedAdmissionObj = {
                    ...updatedAdmissionObj,
                    flag111: the111flag?.checked,
                };
            }

            if (the111flag?.when111 && the111flag?.checked) {
                updatedAdmissionObj = {
                    ...updatedAdmissionObj,
                    flag111Timestamp: moment(the111flag?.when111),
                };
            }

            if (majorTraumaFlag) {
                updatedAdmissionObj = {
                    ...updatedAdmissionObj,
                    majorTraumaFlag: majorTraumaFlag?.checked,
                };
            }
            dispatch(updateAdmissionAttendanceForm(updatedAdmissionObj));
        }
    } catch (e) {
        console.error(e);
    } finally {
        dispatch(setIsLoadingActivity(false));
    }
};

// Old addAdmit
export const admitPatient = (admitObj, onBack, returnAfterRegistry, fromScheduler) => async (dispatch, getState) => {
    const patientOnWhiteboard = getState().admission.patientActiveAttendance;
    const bedBookingForm = getState().admission.admissionBedBookingForm;

    const temporaryData = getState().admission.temporaryAdmissionData;
    const splitScreen = getState().patientContext.splitScreen;
    const hospital = getState().admission.selectedHospital;
    const schedulerHelperState = getState().appointments.modalHelperState;
    const appointmentSlotRef = getState().admission.appointmentSlotRef;
    const selectedActivityToLink = getState().admission.selectedActivityToLink;

    // Admission flags
    const isFastRegistry = getState().admission.isFastRegistry;
    const isSpeedRegistry = getState().admission.isSpeedRegistry;
    const isBedBooking = getState().admission.isBedBooking;
    const isBedas = getState().admission.isBedas;
    const isSDEC = getState().admission.isSDEC;
    const isUTC = getState().admission.isUTC;
    const isReAttendance = getState().admission.isReAttendance;
    const isAppointment = getState().admission.isAppointment;
    const isTemporaryPatientEdit = getState().admission.isTemporaryPatientEdit;
    const isAddOnlyPatient = getState().admission.isOnlyAddPatient;
    const isOnlyDemographic = getState().admission.isOnlyDemographic;
    const isEditFormList = getState().admission.isEditFormList;
    const isUpdateActivity = getState().admission.isUpdateActivity;
    const isMergeActive = getState().admission.isMergeActive;
    const isEditFullPatientData = getState().patient.editFullPatientData;

    let admitTmp;

    const getValidSdecObject = (sdec) => {
        let validObj = { ...sdec };
        delete validObj._id;
        delete validObj.description;
        return validObj;
    };

    // PATIENT DATA
    if (!isFastRegistry && !isSpeedRegistry) {
        admitTmp = dispatch(getRequestDemographicsData(admitObj, isEditFullPatientData));

        // ATTENDANCE DATA
        if (!isBedBooking) {
            admitTmp = {
                ...admitTmp,
                ...dispatch(handleGetAttendanceValue(admitObj, 'createAttendance')),
            };
        }

        if (isBedBooking) {
            admitTmp = {
                patient: {
                    ...admitTmp.patient,
                },
            };

            admitTmp = {
                ...admitTmp,
                csmContactedTime: bedBookingForm,
            };

            if (admitObj.specialty) {
                admitTmp = {
                    ...admitTmp,
                    specialty: admitObj.specialty,
                };
            }

            if (admitObj.triage) {
                admitTmp = {
                    ...admitTmp,
                    triage: admitObj.triage,
                };
            }

            if (admitObj.alarms) {
                if (!_.isEmpty(admitObj.alarms[0])) {
                    admitTmp = {
                        ...admitTmp,
                        alarms: admitObj.alarms,
                    };
                }
            }

            if (admitObj.currentSiteBedBooking) {
                admitTmp = {
                    ...admitTmp,
                    currentSiteBedBooking: admitObj.currentSiteBedBooking,
                };
            }

            if (admitObj.sdecSite) {
                admitTmp = {
                    ...admitTmp,
                    sdecSite: getValidSdecObject(admitObj.sdecSite),
                };
            }
        }
    }

    if (isFastRegistry || isSpeedRegistry) {
        let tag = isFastRegistry ? PATIENT_TAGS.FAST_REGISTRY : PATIENT_TAGS.TEMPORARY;
        admitTmp = {
            speedRegistry: isSpeedRegistry || admitObj.speedRegistry,
            patient: {
                tags: [tag],
                ...dispatch(handleGetPatientValue(admitObj, 'temporaryFastAttendance')),
            },
            ...dispatch(handleGetAttendanceValue(admitObj, 'temporaryFastAttendance')),
        };
    }

    if (!isFastRegistry && !isSpeedRegistry && admitObj.patientUnknown) {
        admitTmp['patient']['tags'] = _.isEmpty(admitTmp?.patient?.tags)
            ? [PATIENT_TAGS.UNKNOWN]
            : [...admitTmp.patient.tags, PATIENT_TAGS.UNKNOWN];
    }

    // Update attendance flag if patient is on whiteboard
    if (patientOnWhiteboard && admitTmp.admitDate) {
        admitTmp = {
            ...admitTmp,
            patient: {
                ...dispatch(handleGetPatientValue(admitObj, 'updateAttendance')),
            },
            ...dispatch(handleGetAttendanceValue(admitObj, 'updateAttendance')),
        };
    }

    if (isAddOnlyPatient) {
        admitTmp = {
            patient: {
                ...dispatch(handleGetPatientValue(admitObj, 'createPatient')),
            },
            ...dispatch(handleGetAttendanceValue(admitObj, 'createOnlyPatient')),
        };
    }

    if (temporaryData?.patient) {
        admitTmp = {
            ...dispatch(handleGetAttendanceValue(admitObj, 'updateTemporaryPatient')),
            patient: {
                ...dispatch(handleGetPatientValue(admitObj, 'updateTemporaryPatient')),
            },
        };
    }

    if (temporaryData?.attendance) {
        admitTmp = {
            ...admitTmp,
            patient: {
                ...admitTmp.patient,
                ...dispatch(handleGetPatientValue(admitObj, 'updateAttendance')),
            },
            ...dispatch(handleGetAttendanceValue(admitObj, 'updateAttendance')),
        };
    }

    if (hospital) {
        admitTmp = {
            ...admitTmp,
            facility: hospital?.__id,
            odsCode: hospital?.__id,
        };
    }

    if (admitTmp?.cpis && _.isNil(admitTmp?.cpisAlert)) {
        admitTmp.cpisAlert = false;
    }
    if (!admitTmp?.cpis && !_.isNil(admitTmp?.cpisAlert)) {
        delete admitTmp.cpisAlert;
    }

    if (!isBedBooking) {
        // Add an Admit flag
        admitTmp.reAttendance = isReAttendance;
        admitTmp.sdec = isSDEC;
        admitTmp.bedas = isBedas;
        admitTmp.utc = isUTC;
        admitTmp.linkedAttendanceId = selectedActivityToLink;
        admitTmp.attendanceUpdate = isUpdateActivity || isMergeActive || patientOnWhiteboard || isEditFullPatientData;
        // admitTmp.speedRegistry = null // TODO
    }

    try {
        dispatch(setLoadingGlobal(true));

        if (admitObj?.wardId) {
            admitTmp.wardCode = admitObj.wardId;
        }

        if (isBedBooking) {
            if (admitTmp?.odsCode) {
                delete admitTmp?.odsCode;
            }

            await API.admitWithBedBooking(admitTmp).then(() => {
                dispatch(
                    sendUserAudit({ patient: admitTmp?.patient }, AUDIT_TYPES.ADMIT_PATIENT, {
                        admitData: admitTmp,
                        admitWithBedBooking: true,
                    }),
                );

                dispatch(setAdmissionStep(0));
                dispatch(
                    setSnackbar({
                        type: 'success',
                        message: 'Patient successfully added to the bed booking system.',
                    }),
                );
            });
        } else if (isMergeActive && temporaryData?.patient) {
            const asArray = Object.entries(admitTmp.patient);
            const filtered = asArray.filter(([key, value]) => key !== 'hospitalNumber' && key !== '__id');
            if (admitTmp?.patient?.tags?.includes(PATIENT_TAGS.TEMPORARY)) {
                admitTmp.patient.tags = _.filter(admitTmp?.patient?.tags, (tag) => tag !== PATIENT_TAGS.TEMPORARY);
            }

            const admitObjectMerge = {
                ...admitTmp,
                patient: {
                    ...Object.fromEntries(filtered),
                },
            };

            if (hospital?.hospitalId) {
                admitTmp = {
                    ...admitTmp,
                    odsCode: hospital?.hospitalId,
                };
            }

            await API.admitWithMerge(admitTmp).then(() => {
                dispatch(
                    sendUserAudit({ patient: admitTmp?.patient }, AUDIT_TYPES.MERGE_PATIENT, {
                        admitData: admitTmp,
                        admitWithMerge: true,
                    }),
                );
                dispatch(setAdmissionStep(0));
                dispatch(
                    setSnackbar({
                        type: 'success',
                        message: 'Patient merged successfully. The changes will be applied within a few minutes.',
                    }),
                );
            });
        } else {
            if (hospital?.hospitalId) {
                admitTmp = {
                    ...admitTmp,
                    odsCode: hospital?.hospitalId,
                };
            }

            let blockAdmit = false;
            // Check if patient has active activities before admitting. Only if not Fast or Speed registry and updating activity
            if (!isFastRegistry && !isSpeedRegistry && !isUpdateActivity && !isOnlyDemographic && !isEditFormList) {
                const checkActivities = await API.checkPatientActivities(admitTmp?.patient?.__id);
                const onlyActive = _.filter(checkActivities, (act) => !act.endDate && act.attendanceType !== 'I');
                blockAdmit = onlyActive && onlyActive?.length > 0;
            }

            if (blockAdmit) {
                dispatch(setOpenPatientWarningActiveActivityDialog(true));
                return null;
            } else {
                return API.patientAdmission(admitTmp)
                    .then((res) => {
                        dispatch(
                            sendUserAudit({ patient: admitTmp?.patient }, AUDIT_TYPES.ADMIT_PATIENT, {
                                admitData: admitTmp,
                                patientAdmission: true,
                                TemporaryAdmission: admitTmp?.speedRegistry,
                            }),
                        );
                        dispatch(
                            setSnackbar({
                                type: 'success',
                                message: isAddOnlyPatient
                                    ? 'Patient has been successfully created.'
                                    : `Attendance has been ${admitTmp.attendanceUpdate ? 'updated' : 'registered'} successfully.`,
                            }),
                        );

                        if (isAppointment && !!res) {
                            const id = appointmentSlotRef?.__id ?? schedulerHelperState.__id;

                            if (id) {
                                dispatch(updateAppointmentStatus(id, SLOT_STATUSES.ARRIVED.value, () => _.isFunction(onBack) && onBack()));
                            }
                        }

                        if (returnAfterRegistry && onBack) onBack();
                        // TODO MAteusz do sprawdzeni gdzie to jest?
                        // if (returnAfterRegistry) history.goBack();
                        dispatch(setAdmissionStep(0));

                        return res;
                    })
                    .catch((err) => {
                        console.error(err);
                        dispatch(
                            setSnackbar({
                                type: 'error',
                                message: 'An error occurred while registering the patient.',
                            }),
                        );
                    })
                    .finally(() => {
                        if (splitScreen?.enabled) {
                            dispatch(toggleSplitScreen());
                        }
                    });
            }
        }
    } catch (error) {
        if (error?.response?.status === 403) {
            dispatch(
                setSnackbar({
                    message: 'You do not have permission to perform this operation.',
                    type: 'error',
                }),
            );
            return;
        }
        console.error(error);
        setSnackbar({ type: 'error', message: 'Failed to create attendance. Please try again.' });
    } finally {
        dispatch(setLoadingGlobal(false));
    }
};

export const getLastAttendance = (patient) => async (dispatch, getState) => {
    const tempPatientRef = getState().admission.temporaryAdmissionData;
    if (patient && patient?.activeAttendances) {
        const lastActiveAttendance = patient.activeAttendances[patient.activeAttendances.length - 1];
        const patientAttendanceData = {
            attendanceId: lastActiveAttendance.attendanceId,
            patientId: patient.hospitalNumber || patient.__id,
        };

        try {
            const attendance = await API.getPatientAttendance(patientAttendanceData)
                .then((res) => res[0])
                .catch((e) => {
                    dispatch(setAdmissionTempPt(patient));
                    dispatch(setOpenAttendanceErrorDialog(true));
                    console.error(`getPatientAttendance Pt ID ${patient?.__id}`, e);
                });
            dispatch(
                updateTemporaryAdmissionData({
                    attendance,
                }),
            );
            if (attendance) {
                // ZOBACZ TUTAJ
                dispatch(getAdmissionAttendance(tempPatientRef?.patient));
            }
        } catch (e) {
            console.log(e);
        }
    }
};

export const handleEditTemporaryPatient = (state) => async (dispatch, getState) => {
    if (!state.temporaryId && state.temporaryId === state.validId) {
        dispatch(
            setSnackbar({
                type: 'error',
                message: 'Temporary Patient ID is required to proceed.',
            }),
        );
        return;
    }

    try {
        if (state?.temporaryId && !state?.validId) {
            await dispatch(findPatient(state.temporaryId)).then((patient) => {
                if (!patient) return;

                if (
                    !patient.tags ||
                    (patient.tags && !patient.tags?.includes(PATIENT_TAGS.TEMPORARY) && !patient.tags?.includes(PATIENT_TAGS.UNKNOWN))
                ) {
                    dispatch(
                        setSnackbar({
                            type: 'error',
                            message: 'Selected patient is not temporary. Please select a temporary patient to proceed.',
                        }),
                    );
                    return;
                }

                dispatch(selectAdmissionPatient(patient));
                dispatch(updateTemporaryAdmissionData({ patient: patient }));
                dispatch(setPatientSelected(true));
                dispatch(setIsTemporaryPatientEdit(true));
                dispatch(setOpenTemporaryPatientEditDialog(false));
                return patient;
            });
        }
        if (state?.temporaryId && state?.validId) {
            dispatch(getBothPatients(state)).then((patients) => {
                if (patients[0] && !checkIfPatientIsTemporary(patients[0])) {
                    dispatch(
                        setSnackbar({
                            type: 'error',
                            message: 'Selected patient is not a temporary patient. Please select a temporary patient to proceed.',
                        }),
                    );
                    return;
                }

                dispatch(selectAdmissionPatient(patients[1]));
                dispatch(getLastAttendance(patients[0]));
                dispatch(updateTemporaryAdmissionData({ patient: patients[0] }));
                dispatch(updateAdmissionMergeForm(INIT_MERGE_ADMISSION_STATE));
                dispatch(setOpenTemporaryPatientEditDialog(false));
            });
        }
    } catch (e) {
        console.error('Something went wrong. Please try again later.', e);
    }
};

const findPatient = (patientId) => async (dispatch, getState) => {
    try {
        const patient = await API.getPatientByID(patientId);
        if (!patient) {
            dispatch(
                setSnackbar({
                    type: 'error',
                    message: `No patient found with the provided ID, ${patientId}`,
                }),
            );
            return;
        }
        return patient;
    } catch (error) {
        dispatch(
            setSnackbar({
                type: 'error',
                message: 'Error finding patient. Please check the ID and try again.',
            }),
        );
    }
};

const getBothPatients = (state) => async (dispatch, getState) => {
    const temporaryId = state.temporaryId;
    const validId = state.validId;
    if (temporaryId === validId) {
        dispatch(
            setSnackbar({
                type: 'error',
                message: 'Patient ID cannot be used for both temporary and valid patient.',
            }),
        );
        return;
    }

    try {
        return await Promise.all([dispatch(findPatient(temporaryId)), dispatch(findPatient(validId))]);
    } catch (e) {
        dispatch(
            setSnackbar({
                type: 'error',
                message: 'Error occurred while retrieving patient information. Please try again.',
            }),
        );
    }
};

const handleGetPatientValue = (object, mode) => (dispatch, getState) => {
    let objToReturn = {};
    const patientNumbers = getState().admission.temporaryAdmissionData?.patientNumbers;
    const patientAge = moment().diff(object?.dob, 'years'); // TODO @Mateusz check

    const getAddresses = (object) => {
        let addresses = [];
        if (!RequestDataParser.getAddressLines(object) && !RequestDataParser.getAddressLines(object, true)) {
            return null;
        }

        if (RequestDataParser.getAddressLines(object)) {
            addresses.push({
                typeName: 'Home',
                addressLines: RequestDataParser.getAddressLines(object),
                postalCode: object.postalCode,
            });
        }

        if (
            !_.isNil(object.streetTemporary) ||
            !_.isNil(object.postalCodeTemporary) ||
            object.streetTemporary ||
            object.postalCodeTemporary
        ) {
            addresses.push({
                typeName: 'Temporary',
                addressLines: RequestDataParser.getAddressLines(object, true),
                postalCode: object.postalCodeTemporary,
            });
        }
        return addresses;
    };

    const getSchool = () => {
        if (patientAge >= 18) {
            return null;
        }
        return RequestDataParser.mapSchoolObject(object);
    };

    let ehicCardObj = {
        number: object.ehicNumber,
        type: 'EhicNumber',
        simpleType: null,
        incomingSystem: null,
    };

    if ((mode === 'updateAttendance' || mode === 'updatePatient') && patientNumbers && object?.ehicNumber) {
        let localFromArr = patientNumbers && patientNumbers.find((item) => item.type === 'EhicNumber' || item.simpleType === 'EhicNumber');

        if (localFromArr) {
            ehicCardObj = {
                ...localFromArr,
                number: object.ehicNumber,
            };
        }
    }

    const ptNames = _.cloneDeep(object?.names);

    if (ptNames) {
        const validKeys = ['startDate', 'middleNames', 'nameType', 'title', 'endDate', 'firstName', 'familyName', '__id', 'initials'];

        for (let ptName of ptNames) {
            for (let key in ptName) {
                if (!validKeys.includes(key)) delete ptName[key];
            }
        }
    }

    const patientToRequestMap = new Map([
        ['firstName', object.firstName],
        ['middleNames', object.middleNames],
        ['familyName', object.familyName],
        ['dob', RequestDataParser.getFormattedDOB(object)],
        ['hospitalNumber', object.hospitalNumber],
        ['__id', object.hospitalNumber],
        ['nhsNumber', object.nhsNumber],
        ['gp', RequestDataParser.getGpId(object)],
        ['surgery', RequestDataParser.getSurgeryId(object)],
        ['genderCode', object?.gender?.__code],
        ['primaryLanguageCode', object?.spokenLanguage?.__code],
        ['interpreterLanguageCode', object?.interpreterLanguage?.__code],
        ['interpreterRequired', object?.patientNeedsInterpreter !== null ? Boolean(object?.patientNeedsInterpreter) : null],
        ['interpreterRefused', object?.patientNeedsInterpreter === null],
        ['accommodationStatus', object?.accommodationStatus || null],
        ['addresses', getAddresses(object)],
        ['postalCode', object.postalCode],
        // ["names", object?.names],
        ['names', ptNames],
        ['ethnicGroup', object?.ethnicGroup?.__id ? { __id: object?.ethnicGroup?.__id } : null],
        ['addressLines', RequestDataParser.getAddressLines(object)],
        ['phoneNumbers', RequestDataParser.getPhoneNumbers(object)],
        ['emails', RequestDataParser.getEmails(object)],
        ['nextOfKins', RequestDataParser.mapNextOfKinObject(object)],
        ['tags', object?.tags ? object?.tags : []],
        ['school', getSchool()],
        ['patientNumbers', object.ehicCard && object.ehicNumber ? [ehicCardObj] : null],
        ['overseasVisitorChargingCategory', object.overseasStatus ? object.overseasStatus : null],
    ]);

    const modeValues = {
        selectPatient: ['firstName', 'familyName', 'dob', 'hospitalNumber', '__id'],
        temporaryFastAttendance: ['firstName', 'familyName', 'dob', 'genderCode', '__id', 'hospitalNumber'],
        updateTemporaryPatient: [
            'firstName',
            'middleNames',
            'familyName',
            'dob',
            'nhsNumber',
            'genderCode',
            'gp',
            'surgery',
            'primaryLanguageCode',
            'interpreterRequired',
            'addresses',
            'postalCode',
            'ethnicGroup',
            'addressLines',
            'phoneNumbers',
            'emails',
            'nextOfKins',
            'school',
            'patientNumbers',
            'overseasVisitorChargingCategory',
            'interpreterRefused',
            'interpreterLanguageCode',
            'accommodationStatus',
            'tags',
        ],
        createPatient: [
            'firstName',
            'patientNumbers',
            'middleNames',
            'familyName',
            'nhsNumber',
            'dob',
            'gp',
            'surgery',
            'genderCode',
            'addressLines',
            'postalCode',
            'addresses',
            'ethnicGroup',
            'phoneNumbers',
            'emails',
            'nextOfKins',
            'school',
            'primaryLanguageCode',
            'interpreterRequired',
            'overseasVisitorChargingCategory',
            'interpreterRefused',
            'interpreterLanguageCode',
            'accommodationStatus',
            'tags',
        ],
        updatePatient: [
            'firstName',
            'middleNames',
            'familyName',
            'dob',
            'nhsNumber',
            'hospitalNumber',
            '__id',
            'genderCode',
            'gp',
            'surgery',
            'primaryLanguageCode',
            'interpreterRequired',
            'addresses',
            'postalCode',
            'ethnicGroup',
            'addressLines',
            'phoneNumbers',
            'emails',
            'nextOfKins',
            'school',
            'patientNumbers',
            'overseasVisitorChargingCategory',
            'interpreterRefused',
            'interpreterLanguageCode',
            'accommodationStatus',
            'tags',
            'names',
        ],
        updateAttendance: [
            'firstName',
            'middleNames',
            'familyName',
            'dob',
            'nhsNumber',
            'hospitalNumber',
            '__id',
            'genderCode',
            'gp',
            'surgery',
            'primaryLanguageCode',
            'interpreterRequired',
            'addresses',
            'postalCode',
            'ethnicGroup',
            'addressLines',
            'phoneNumbers',
            'emails',
            'nextOfKins',
            'school',
            'patientNumbers',
            'overseasVisitorChargingCategory',
            'interpreterRefused',
            'interpreterLanguageCode',
            'accommodationStatus',
            'tags',
            'names',
        ],
    };

    modeValues[mode].forEach((key) => {
        const value = patientToRequestMap.get(key);
        if (!_.isNil(value) && value !== '') objToReturn[key] = value;
        if (key === 'addressLines') delete objToReturn[key];
    });

    return objToReturn;
};

const handleGetAttendanceValue = (object, mode) => (dispatch, getState) => {
    const tempPatient = getState().admission.temporaryAdmissionData?.patient;
    const isBedas = getState().admission.isBedas;

    const handleRefreshPatientFlags = (attendanceData) => {
        let patientFlags = [
            {
                name: '111',
                checked: attendanceData?.flag111,
                when111: moment.isMoment(attendanceData?.flag111Timestamp) ? attendanceData?.flag111Timestamp.toISOString() : null,
                date: moment().toISOString(),
            },
            {
                name: 'majorTrauma',
                checked: attendanceData?.majorTraumaFlag,
                by: null,
                date: moment().toISOString(),
            },
        ];

        if (!_.isEmpty(attendanceData?.patientFlags)) {
            patientFlags = _.cloneDeep(attendanceData.patientFlags);

            const the111FlagIndex = patientFlags?.findIndex((flag) => flag?.name === '111');
            const majorTraumaFlagIndex = patientFlags?.findIndex((flag) => flag?.name === 'majorTrauma');
            const bedasFlagIndex = patientFlags?.findIndex((flag) => flag?.name === 'bedas');

            if (the111FlagIndex !== -1) {
                patientFlags[the111FlagIndex].checked = attendanceData?.flag111;

                if (attendanceData?.flag111Timestamp && moment.isMoment(attendanceData?.flag111Timestamp)) {
                    patientFlags[the111FlagIndex].when111 = attendanceData?.flag111Timestamp.toISOString();
                }
            }

            if (majorTraumaFlagIndex !== -1) {
                patientFlags[majorTraumaFlagIndex].checked = attendanceData?.majorTraumaFlag;
                patientFlags[majorTraumaFlagIndex].date = moment().toISOString();
            }

            if (bedasFlagIndex !== -1 && isBedas) {
                patientFlags[bedasFlagIndex].checked = true;
            }
        }

        return patientFlags;
    };

    const getMergePatientObject = (obj) => {
        if (!tempPatient) return;
        let temp = {
            from: tempPatient?.hospitalNumber || null,
        };

        if (tempPatient?.hospitalNumber && tempPatient.hospitalNumber !== obj.hospitalNumber) {
            temp = {
                ...temp,
                to: obj.hospitalNumber,
            };
        }
        return temp;
    };

    const attendanceMap = new Map([
        ['chiefComplaintCode', object.chiefComplaint?.snomedCode],
        ['historyOfPresentingComplaint', object.historyOfPresentingComplaint],
        ['ehicNotProvidedReason', !object.ehicCard ? object.ehicNotProvidedReason : null],
        ['admitDate', moment(object?.admitDate)?.toISOString()],
        ['arrivalModeCode', object.modeOfArrival?.snomedCode],
        ['attendanceSourceCode', object.attendanceSource?.snomedCode],
        ['wardCode', object?.ward?.wardCode],
        ['edActivityType', object?.edActivityType?.__code],
        ['locationCode', object.location?.__code],
        ['attendanceTypeCode', object.attendanceType?.__code],
        ['ambulanceIncidentNumber', object.ambulanceIncidentNumber],
        ['conveyingAmbulanceOrgId', object.conveyingAmbulanceOrgId],
        ['careContactIdentifier', object.careContactIdentifier],
        ['cpis', object.cpIs],
        ['cpisAlert', object.cpIsAlertPresent],
        ['occupation', object.occupation],
        ['majorTraumaFlag', object.majorTraumaFlag],
        ['the111flag', object.flag111],
        ['the111flagDate', object.flag111Timestamp?.toISOString()],
        ['households', RequestDataParser.mapHouseholdObject(object)],
        ['escorts', RequestDataParser.mapEscortObject(object)],
        ['mergeRequest', getMergePatientObject(object)],
        ['employerName', object.employerName],
        [
            'employerAddress',
            object.employerAddress
                ? {
                      addressLines: [object.employerAddress],
                      postalCode: object.employerPostalCode,
                  }
                : null,
        ],
        ['attendanceId', object.lastAttendanceId],
        ['attendanceCategory', object.attendanceCategory],
        ['patientFlags', handleRefreshPatientFlags(object)],
        ['attendanceAssaultRelated', object?.assaultRelated],
        ['feedback', object?.feedback],
    ]);

    const modeValues = {
        temporaryFastAttendance: [
            'historyOfPresentingComplaint',
            'chiefComplaintCode',
            'admitDate',
            'arrivalModeCode',
            'ambulanceIncidentNumber',
            'conveyingAmbulanceOrgId',
            'careContactIdentifier',
            'wardCode',
            'edActivityType',
        ],
        // 'updateTemporaryPatient': ['admitDate', 'historyOfPresentingComplaint', 'chiefComplaintCode', 'arrivalModeCode', 'ehicNotProvidedReason', 'ambulanceIncidentNumber', 'cpis', 'cpisAlert', 'occupation', 'households', 'escorts', 'employerName', 'employerAddress', 'mergeRequest', 'conveyingAmbulanceOrgId', 'careContactIdentifier'],
        updateTemporaryPatient: [
            'mergeRequest',
            'chiefComplaintCode',
            'historyOfPresentingComplaint',
            'ehicNotProvidedReason',
            'admitDate',
            'arrivalModeCode',
            'attendanceSourceCode',
            'locationCode',
            'attendanceTypeCode',
            'ambulanceIncidentNumber',
            'conveyingAmbulanceOrgId',
            'careContactIdentifier',
            'cpis',
            'cpisAlert',
            'occupation',
            'majorTraumaFlag',
            'the111flag',
            'the111flagDate',
            'households',
            'escorts',
            'employerName',
            'employerAddress',
            'attendanceId',
            'attendanceCategory',
            'patientFlags',
            'attendanceAssaultRelated',
        ],
        createOnlyPatient: [
            'ehicNotProvidedReason',
            'ambulanceIncidentNumber',
            'cpis',
            'cpisAlert',
            'occupation',
            'households',
            'escorts',
            'employerName',
            'employerAddress',
            'mergeRequest',
            'conveyingAmbulanceOrgId',
            'careContactIdentifier',
        ],
        updatePatient: [
            'cpis',
            'cpisAlert',
            'ehicNotProvidedReason',
            'occupation',
            'households',
            'escorts',
            'employerName',
            'employerAddress',
        ],
        createAttendance: [
            'historyOfPresentingComplaint',
            'chiefComplaintCode',
            'ehicNotProvidedReason',
            'admitDate',
            'arrivalModeCode',
            'attendanceSourceCode',
            'locationCode',
            'attendanceTypeCode',
            'ambulanceIncidentNumber',
            'cpis',
            'cpisAlert',
            'occupation',
            'majorTraumaFlag',
            'the111flag',
            'the111flagDate',
            'households',
            'escorts',
            'employerName',
            'employerAddress',
            'conveyingAmbulanceOrgId',
            'careContactIdentifier',
            'attendanceCategory',
            'attendanceAssaultRelated',
            'feedback',
            'wardCode',
            'edActivityType',
        ],
        // "updateAttendance": ['attendanceId', 'admitDate', 'historyOfPresentingComplaint', 'chiefComplaintCode', 'ehicNotProvidedReason', 'arrivalModeCode', 'attendanceSourceCode', 'locationCode', 'attendanceTypeCode', 'ambulanceIncidentNumber', 'cpis', 'cpisAlert', 'occupation', 'majorTraumaFlag', 'the111flag', 'the111flagDate', 'households', 'escorts', 'employerName', 'employerAddress', 'conveyingAmbulanceOrgId', 'careContactIdentifier'],
        updateAttendance: [
            'chiefComplaintCode',
            'historyOfPresentingComplaint',
            'ehicNotProvidedReason',
            'admitDate',
            'arrivalModeCode',
            'attendanceSourceCode',
            'locationCode',
            'attendanceTypeCode',
            'ambulanceIncidentNumber',
            'conveyingAmbulanceOrgId',
            'careContactIdentifier',
            'cpis',
            'cpisAlert',
            'occupation',
            'majorTraumaFlag',
            'the111flag',
            'the111flagDate',
            'households',
            'escorts',
            'employerName',
            'employerAddress',
            'attendanceId',
            'attendanceCategory',
            'patientFlags',
            'attendanceAssaultRelated',
            'feedback',
            'wardCode',
            'edActivityType',
        ],
    };
    let valueToReturn = {};
    modeValues[mode].forEach((key) => {
        const value = attendanceMap.get(key);
        if (value) {
            valueToReturn[key] = value;
        }
    });

    return valueToReturn;
};

export const getRequestDemographicsData =
    (admitObj, patientUpdate = false) =>
    (dispatch, getState) => {
        const selectedHospital = getState().admission.selectedHospital;
        const isTemporaryPatientEdit = getState().admission.isTemporaryPatientEdit;

        let admitTmp;

        if (admitObj.hospitalNumber && !patientUpdate) {
            admitTmp = {
                patientUpdate: false,
                patient: {
                    ...dispatch(handleGetPatientValue(admitObj, 'selectPatient')),
                },
            };
        }

        if (patientUpdate || !admitObj.hospitalNumber || (admitObj.hospitalNumber && isTemporaryPatientEdit)) {
            admitTmp = {
                ...admitTmp,
                patient: {
                    ...dispatch(handleGetPatientValue(admitObj, 'createPatient')),
                },
            };

            if (admitObj.ehicCard !== null) {
                switch (admitObj.ehicCard) {
                    case true:
                        admitTmp = {
                            ...admitTmp,
                            patient: {
                                ...admitTmp.patient,
                                patientNumbers: [
                                    {
                                        number: admitObj.ehicNumber,
                                        type: 'EhicNumber',
                                        simpleType: null,
                                        incomingSystem: null,
                                    },
                                ],
                            },
                        };
                        break;
                    case false:
                        admitTmp = {
                            ...admitTmp,
                            ehicNotProvidedReason: admitObj.noEhicReason,
                        };
                        break;
                    default:
                        break;
                }
            }

            if (patientUpdate) {
                admitTmp = {
                    patientUpdate: true,
                    patient: {
                        ...admitTmp.patient,
                        ...dispatch(handleGetPatientValue(admitObj, 'updatePatient')),
                    },
                    ...dispatch(handleGetAttendanceValue(admitObj, 'updatePatient')),
                };
            }
            if (admitTmp?.cpis && _.isNil(admitTmp?.cpisAlert)) {
                admitTmp.cpisAlert = false;
            }
            if (!admitTmp?.cpis && !_.isNil(admitTmp?.cpisAlert)) {
                delete admitTmp.cpisAlert;
            }
        }

        if (selectedHospital) {
            admitTmp = {
                ...admitTmp,
                odsCode: selectedHospital?.__id,
            };
        }
        return admitTmp;
    };

/*
 * Registration flow
 * */

export const registrationFlow = (flowType) => (dispatch, getState) => {
    const cratePatientDialog = getState().admission.createPatientAdmission;
    let admissionStep = 1;

    // Select Patient
    if (cratePatientDialog?.patient) {
        dispatch(selectAdmissionPatient(cratePatientDialog?.patient));
    }

    switch (flowType) {
        case REGISTRATION_FLOW_TYPE.NEW_VISIT:
        case REGISTRATION_FLOW_TYPE.FOLLOW_UP:
        case REGISTRATION_FLOW_TYPE.FOLLOW_UP_BEDAS:
        case REGISTRATION_FLOW_TYPE.FOLLOW_UP_SDEC:
            if (!cratePatientDialog.openDialog) {
                dispatch(setIsOnlyAddPatient(true));
            }
            break;
        case REGISTRATION_FLOW_TYPE.UPDATE_DEMOGRAPHICS:
            dispatch(setIsOnlyDemographic(true));
            break;
        case REGISTRATION_FLOW_TYPE.BED_BOOKING:
            dispatch(setIsBedBooking(true));
            break;
        case REGISTRATION_FLOW_TYPE.UPDATE_VISIT:
            dispatch(setIsUpdateActivity(true));
            break;
        case REGISTRATION_FLOW_TYPE.RE_ATTENDANCE:
            dispatch(setIsReAttendance(true));
            if (!cratePatientDialog.openDialog) {
                dispatch(setIsOnlyAddPatient(true));
            }
            break;
        case REGISTRATION_FLOW_TYPE.CREATE_TCI:
        case REGISTRATION_FLOW_TYPE.FOLLOW_UP_SELECT:
        case REGISTRATION_FLOW_TYPE.MERGE_PATIENT:
        default:
            console.warn('Not implemented', flowType);
    }

    if (!cratePatientDialog.openDialog) {
        dispatch(setIsOnlyAddPatient(true));
    }

    dispatch(setAdmissionStep(admissionStep));
    dispatch(setMergeQueuePatient(null));
};

export const handleMergeTemporary = () => async (dispatch, getState) => {
    const cratePatientDialog = getState().admission.createPatientAdmission;
    const mergeQueuePatient = getState().patient.mergeQueuePatient;
    const isSame = getState().admission.isSamePatientToMerge;

    const state = {
        temporaryId: mergeQueuePatient?.patient?.__id,
        validId: isSame ? null : cratePatientDialog?.patient?.__id,
    };
    let mergeToNewPatient = false;

    // here add state to check if handles merge to the newly created patient
    if (isSame) {
        mergeToNewPatient = true;
    }
    await dispatch(handleEditTemporaryPatient(state));
    dispatch(
        updateAdmissionMergeForm({
            mergeActive: true,
            mergeToNewPatient,
        }),
    );
    dispatch(setMergeQueuePatient(null));
};

export const handleGenerateForm = (ptId, form) => async (dispatch, getState) => {
    try {
        const formData = await APIForm.generatePdf(ptId, form);

        if (formData) {
            let objectURL = window.URL.createObjectURL(new Blob([formData], { type: 'application/pdf' }));
            window.open(objectURL, '_blank');
        }
    } catch (err) {
        dispatch(
            setSnackbar({
                type: 'error',
                message: 'The document could not be generated.',
            }),
        );
    }
};

export default admissionSlice.reducer;
